"use client";

import { useEffect, useRef, useState } from "react";
import { fitProductImage, loadImageContentBounds, type ImageContentBounds } from "@/lib/product-image-fit";

/** Fits the product rather than the transparent canvas around its photograph. */
export function ComponentProductImage({ src }: { src: string }) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [frame, setFrame] = useState({ width: 0, height: 0 });
  const [content, setContent] = useState<{
    src: string;
    width: number;
    height: number;
    bounds: ImageContentBounds;
  } | null>(null);
  const requestRef = useRef({ id: 0 });

  useEffect(() => {
    const requests = requestRef.current;
    const frame = frameRef.current;
    if (!frame) return;
    const observer = new ResizeObserver(([entry]) => {
      // Layout dimensions stay independent of React Flow's zoom and rotation.
      setFrame({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(frame);
    return () => {
      observer.disconnect();
      requests.id++;
    };
  }, []);

  const fit = content?.src === src
    ? fitProductImage(content.width, content.height, frame.width, frame.height, content.bounds)
    : null;

  return (
    <div ref={frameRef} className="relative flex-1 min-h-0 mx-2 mb-2 overflow-hidden">
      <img
        src={src}
        alt=""
        loading="lazy"
        draggable={false}
        className="absolute max-w-none pointer-events-none"
        style={fit ?? { inset: 0, width: "100%", height: "100%", objectFit: "contain" }}
        onLoad={(event) => {
          const { naturalWidth: width, naturalHeight: height } = event.currentTarget;
          const request = ++requestRef.current.id;
          void loadImageContentBounds(src).then((bounds) => {
            if (bounds && request === requestRef.current.id) setContent({ src, width, height, bounds });
          });
        }}
      />
    </div>
  );
}
